import React, { useState } from "react";
import { entities, getDisplayMessage, downloadUploadFile } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Eye,
  Download,
  Filter,
  CheckCircle2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import AtestadoForm from "../components/atestados/AtestadoForm";
import BuscaAvancada from "../components/atestados/BuscaAvancada";
import AtestadoCard from "../components/atestados/AtestadoCard";

export default function AtestadosTecnicosPage() {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState("");
  const [dialogFormAberto, setDialogFormAberto] = useState(false);
  const [dialogBuscaAberto, setDialogBuscaAberto] = useState(false);
  const [dialogVisualizarAberto, setDialogVisualizarAberto] = useState(false);
  const [atestadoSelecionado, setAtestadoSelecionado] = useState(null);
  const [atestadoParaExcluir, setAtestadoParaExcluir] = useState(null);
  const [resultadosBusca, setResultadosBusca] = useState(null);

  const { data: atestados = [], isLoading } = useQuery({
    queryKey: ['atestados-tecnicos'],
    queryFn: () => entities.AtestadoTecnico.list('-createdAt')
  });

  const deleteAtestadoMutation = useMutation({
    mutationFn: (id) => entities.AtestadoTecnico.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['atestados-tecnicos'] });
      setAtestadoParaExcluir(null);
      toast.success("Atestado excluído com sucesso");
    },
    onError: (error) => toast.error(getDisplayMessage(error, "Erro ao excluir atestado. Tente novamente.")),
  });

  const handleEditar = (atestado) => {
    setAtestadoSelecionado(atestado);
    setDialogFormAberto(true);
  };

  const handleVisualizar = (atestado) => {
    setAtestadoSelecionado(atestado);
    setDialogVisualizarAberto(true);
  };

  const handleExcluir = (atestado) => {
    setAtestadoParaExcluir(atestado);
  };

  const handleNovo = () => {
    setAtestadoSelecionado(null);
    setDialogFormAberto(true);
  };

  const handleBuscaAvancada = () => {
    setDialogBuscaAberto(true);
  };

  const atestadosFiltrados = resultadosBusca || atestados.filter(atestado => {
    if (!busca) return true;
    const termoBusca = busca.toLowerCase();
    return (
      atestado.instituicao?.toLowerCase().includes(termoBusca) ||
      atestado.resumo_executivo?.toLowerCase().includes(termoBusca) ||
      atestado.tipos?.some(t => t.toLowerCase().includes(termoBusca)) ||
      atestado.competencias_tecnicas?.some(c => c.toLowerCase().includes(termoBusca))
    );
  });

  const estatisticas = {
    total: atestados.length,
    tiposUnicos: [...new Set(atestados.flatMap(a => a.tipos || []))].length,
    competenciasUnicas: [...new Set(atestados.flatMap(a => a.competencias_tecnicas || []))].length
  };

  return (
    <div className="p-4 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-tour="tour-AtestadosTecnicos-root">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Atestados Técnicos
            </h1>
            <p className="text-slate-600">
              Gerencie seu portfólio de atestados e encontre compatibilidade com editais
            </p>
          </div>
          <Button onClick={handleNovo} className="bg-blue-600 hover:bg-blue-700" data-tour="tour-AtestadosTecnicos-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Atestado
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-none shadow-md bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total de Atestados</p>
                  <p className="text-2xl font-bold text-slate-900">{estatisticas.total}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Tipos de Serviço</p>
                  <p className="text-2xl font-bold text-slate-900">{estatisticas.tiposUnicos}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Competências</p>
                  <p className="text-2xl font-bold text-slate-900">{estatisticas.competenciasUnicas}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Filter className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Buscar por instituição, tipo ou competências..."
                  value={busca}
                  onChange={(e) => {
                    setBusca(e.target.value);
                    setResultadosBusca(null);
                  }}
                  className="pl-12 h-12 text-base"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleBuscaAvancada}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Busca Avançada
                </Button>
              </div>
            </div>

            {resultadosBusca && (
              <div className="mt-4 flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700">
                  {resultadosBusca.length} resultado(s) encontrado(s)
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setResultadosBusca(null);
                    setBusca("");
                  }}
                >
                  Limpar busca
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {isLoading ? (
            Array(3).fill(0).map((_, i) => (
              <Card key={i} className="border-none shadow-md animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))
          ) : atestadosFiltrados.length === 0 ? (
            <Card className="border-none shadow-md">
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg mb-4">
                  Nenhum atestado encontrado
                </p>
                <Button onClick={handleNovo} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Primeiro Atestado
                </Button>
              </CardContent>
            </Card>
          ) : (
            atestadosFiltrados.map((atestado) => (
              <AtestadoCard
                key={atestado.id}
                atestado={atestado}
                onEditar={handleEditar}
                onVisualizar={handleVisualizar}
                onExcluir={handleExcluir}
              />
            ))
          )}
        </div>
      </div>

      <Dialog open={dialogFormAberto} onOpenChange={setDialogFormAberto}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {atestadoSelecionado ? 'Editar Atestado' : 'Novo Atestado'}
            </DialogTitle>
            <DialogDescription>
              {atestadoSelecionado 
                ? 'Edite as informações do atestado técnico'
                : 'Adicione um novo atestado técnico ao seu portfólio'
              }
            </DialogDescription>
          </DialogHeader>
          <AtestadoForm
            atestado={atestadoSelecionado}
            onClose={() => {
              setDialogFormAberto(false);
              setAtestadoSelecionado(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialogBuscaAberto} onOpenChange={setDialogBuscaAberto}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Busca Avançada</DialogTitle>
            <DialogDescription>
              Encontre atestados usando busca semântica por tipo, competências e critérios específicos
            </DialogDescription>
          </DialogHeader>
          <BuscaAvancada
            atestados={atestados}
            onResultados={(resultados) => {
              setResultadosBusca(resultados);
              setDialogBuscaAberto(false);
            }}
            onClose={() => setDialogBuscaAberto(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialogVisualizarAberto} onOpenChange={setDialogVisualizarAberto}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Visualizar Atestado</DialogTitle>
            <DialogDescription>
              Detalhes completos do atestado técnico
            </DialogDescription>
          </DialogHeader>
          {atestadoSelecionado && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="font-semibold text-lg mb-2">{atestadoSelecionado.instituicao}</h3>
                {atestadoSelecionado.resumo_executivo && (
                  <p className="text-sm text-slate-600 mb-3">{atestadoSelecionado.resumo_executivo}</p>
                )}
                
                {atestadoSelecionado.tipos && atestadoSelecionado.tipos.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-slate-700 mb-1">Tipos:</p>
                    <div className="flex flex-wrap gap-1">
                      {atestadoSelecionado.tipos.map((tipo, idx) => (
                        <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700">
                          {tipo}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {atestadoSelecionado.competencias_tecnicas && atestadoSelecionado.competencias_tecnicas.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-slate-700 mb-1">Competências:</p>
                    <div className="flex flex-wrap gap-1">
                      {atestadoSelecionado.competencias_tecnicas.map((comp, idx) => (
                        <Badge key={idx} variant="outline" className="bg-green-50 text-green-700">
                          {comp}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                  {atestadoSelecionado.data_emissao && (() => {
                    try {
                      return (
                        <div>
                          <span className="text-slate-600">Emissão:</span>
                          <span className="ml-2 font-medium">{format(new Date(atestadoSelecionado.data_emissao), "dd/MM/yyyy", { locale: ptBR })}</span>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                  {atestadoSelecionado.data_inicio && (() => {
                    try {
                      return (
                        <div>
                          <span className="text-slate-600">Início:</span>
                          <span className="ml-2 font-medium">{format(new Date(atestadoSelecionado.data_inicio), "dd/MM/yyyy", { locale: ptBR })}</span>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                  {atestadoSelecionado.data_fim && (() => {
                    try {
                      return (
                        <div>
                          <span className="text-slate-600">Fim:</span>
                          <span className="ml-2 font-medium">{format(new Date(atestadoSelecionado.data_fim), "dd/MM/yyyy", { locale: ptBR })}</span>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                  {atestadoSelecionado.valor_contrato && (
                    <div>
                      <span className="text-slate-600">Valor:</span>
                      <span className="ml-2 font-medium">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(atestadoSelecionado.valor_contrato)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {atestadoSelecionado.anexo_url && (
                <div className="border-t pt-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="font-medium text-slate-900">
                          {atestadoSelecionado.anexo_nome || 'Documento anexado'}
                        </p>
                        <p className="text-sm text-slate-600">Arquivo PDF disponível</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setDialogVisualizarAberto(false)}>
                  Fechar
                </Button>
                {atestadoSelecionado.anexo_url && (
                  <Button
                    onClick={() => downloadUploadFile(atestadoSelecionado.anexo_url, atestadoSelecionado.anexo_nome || 'atestado.pdf')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Atestado
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!atestadoParaExcluir} onOpenChange={() => setAtestadoParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o atestado de <strong>{atestadoParaExcluir?.instituicao}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAtestadoMutation.mutate(atestadoParaExcluir.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}